import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Mail, Phone, User, Clock, Trash2, Send } from 'lucide-react';
import { Invitation } from '@/hooks/useInvitations';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS, es, de, it } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

interface InvitationCardProps {
  invitation: Invitation;
  onDelete: (id: string) => void;
  onResendEmail: (invitation: Invitation) => void;
}

export const InvitationCard = ({ invitation, onDelete, onResendEmail }: InvitationCardProps) => {
  const { t, i18n } = useTranslation();
  const isExpired = new Date(invitation.expires_at) < new Date();
  const isAccepted = !!invitation.accepted_at;

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      case 'de': return de;
      case 'it': return it;
      default: return enUS;
    }
  };

  const timeLeft = formatDistanceToNow(new Date(invitation.expires_at), {
    addSuffix: true,
    locale: getDateLocale(),
  });

  return (
    <Card className="border border-[#DBE2EA] rounded-xl hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-[#F2AC57]/10 flex items-center justify-center">
              <User className="h-5 w-5 text-[#F2AC57]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#2D3748] font-['Funnel_Sans']">
                {invitation.first_name} {invitation.last_name}
              </h3>
            </div>
          </div>
          <Badge 
            className={`${
              isAccepted
                ? 'bg-[#14A38B]/10 text-[#14A38B] border-[#14A38B]/20'
                : isExpired 
                  ? 'bg-[#FF7171]/10 text-[#FF7171] border-[#FF7171]/20' 
                  : 'bg-[#F2AC57]/10 text-[#F2AC57] border-[#F2AC57]/20'
            } border font-['Funnel_Sans']`}
          >
            {isAccepted
              ? t('invite.statusAccepted')
              : isExpired
                ? t('fleet.invitationDetails.expired')
                : t('fleet.invitationDetails.pending')}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-[#5A6B7A]">
          <Mail size={16} className="text-[#0880AE]" />
          <span className="font-['Funnel_Sans']">{invitation.email}</span>
        </div>
        
        {invitation.phone && (
          <div className="flex items-center gap-2 text-sm text-[#5A6B7A]">
            <Phone size={16} className="text-[#0880AE]" />
            <span className="font-['Funnel_Sans']">{invitation.phone}</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-[#5A6B7A]">
          <Clock size={16} className="text-[#0880AE]" />
          <span className="font-['Funnel_Sans']">
            {isAccepted
              ? t('invite.passwordSet')
              : isExpired
                ? t('fleet.invitationDetails.expired')
                : `${t('fleet.invitationDetails.expires')} ${timeLeft}`}
          </span>
        </div>

        <div className="pt-2 border-t border-[#DBE2EA]">
          <p className="text-xs text-[#87ADD4] font-['Funnel_Sans']">
            {t('fleet.invitationDetails.sentOn')}{' '}
            {new Date(invitation.created_at).toLocaleDateString(i18n.language === 'en' ? 'en-US' : i18n.language === 'es' ? 'es-ES' : i18n.language === 'de' ? 'de-DE' : i18n.language === 'it' ? 'it-IT' : 'fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
        </div>

        <div className="pt-3 border-t border-[#DBE2EA] mt-3 space-y-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full border-[#0880AE] text-[#0880AE] hover:bg-[#0880AE]/10"
            disabled={isExpired || isAccepted}
            onClick={() => onResendEmail(invitation)}
          >
            <Send size={16} className="mr-2" />
            {t('fleet.resendEmail')}
          </Button>
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-[#FF7171] hover:text-[#E65959] hover:bg-[#FF7171]/10"
                disabled={isExpired || isAccepted}
              >
                <Trash2 size={16} className="mr-2" />
                {t('fleet.cancelInvitation')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('fleet.cancelInvitationTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('fleet.cancelInvitationDescription', { 
                    name: `${invitation.first_name} ${invitation.last_name}`,
                    email: invitation.email 
                  })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onDelete(invitation.id)}
                  className="bg-[#FF7171] hover:bg-[#E65959]"
                >
                  {t('fleet.confirmCancel')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
};